<template>
  <div v-if="ctx.subscription" class="subscription-details-view">
    <div>
      <router-link :to="{ name: 'settings-subscriptions' }">
        <lucide-icon name="chevron-left" /> {{ $t("objectNames.subscription", 40) }}
      </router-link> /
      {{ $t(`subscriptions.${ctx.subscription.plan}.name`) }}
    </div>

    <div v-if="ctx.subscription.organization" class="mt-4 text-24">
      <router-link
        :to="{
          name: 'settings-organization',
          params: { organizationSlug: ctx.subscription.organization.slug }
        }"
      >
        <strong>{{ ctx.subscription.organization.name }}</strong>
      </router-link>
    </div>

    <div class="mt-5 subscription-details-view__grid">
      <article class="subscription-details-view__card p-0 subscription-details-view__grid-renewal">
        <div class="p-5 column gap-6" style="overflow: hidden">
          <div>
            <h1 class="label mt-0 mb-3">
              <template v-if="renews">
                {{ $t("headings.nextRenewal") }}
              </template>
              <template v-else-if="ctx.expired">
                {{ $t("headings.expiredAt") }}
              </template>
              <template v-else>
                {{ $t("headings.validUntil") }}
              </template>
            </h1>

            <div class="subscription-details-view__next-payment-date">
              <template v-if="renews">
                {{ ctx.formattedRenewalDate }}
              </template>
              <template v-else-if="ctx.expired">
                {{ ctx.formattedExpiredAtDate }}
              </template>
              <template v-else>
                {{ ctx.formattedValidUntilDate }}
              </template>
            </div>
            <div class="mt-4" v-if="renews">
              {{ $t("texts.renewalNotice", ctx.subscription?.nextRenew?.years ?? 1) }}
            </div>
          </div>

          <div v-if="ctx.expired && ctx.subscription.editable" class="mt-auto">
            <button type="button" class="button" @click="restoreExpiredSubscription()">
              {{ $t("actions.renewSubscription") }}
            </button>
          </div>
          <div v-else class="flex gap-4 mt-auto">
            <router-link
              v-if="ctx.subscription.editable && ctx.subscription.plan == 'organization'"
              class="button"
              :to="{
                name: 'subscription-modify',
                params: { subscriptionId: ctx.subscription.id }
              }"
            >
              {{ $t("actions.modifySubscription") }}
            </router-link>

            <button
              v-if="renews"
              class="button button_secondary"
              type="button"
              @click="cancelSubscription()"
            >
              {{ $t("actions.cancelShort") }}
            </button>
            <template v-else-if="!ctx.expired">
              <button
                v-if="ctx.subscription.editable"
                class="button button_secondary"
                type="button"
                @click="reactivateSubscriptionRenewal()"
              >
                {{ $t("actions.enableRenewal") }}
              </button>
              <template 
                v-else-if="ctx.subscription.usesStandardPrices"
              >
                <button
                  v-if="!subscriptionRenewalRequest"
                  class="button button_secondary"
                  type="button"
                  @click="requestExtensionQuote()"
                >
                  {{ $t("actions.requestExtensionQuote") }}
                </button>
              </template>
            </template>
          </div>
        </div>
        <div class="background-dark p-6" v-if="renews">
          <template v-if="ctx.subscription.usesStandardPrices">
            <total-costs
              v-if="ctx.plan"
              :plan="ctx.plan"
              :seats="ctx.subscription.nextRenew.seats"
              :upgrades="ctx.subscription.nextRenew.upgrades"
              :years="ctx.subscription.nextRenew.years"
              :include-vat="true"
              :vat="0.19"
              :borderless="true"
              :compact="true"
            />
            <div class="mt-4 subscription-details-view__auto-payment" v-if="ctx.subscription.paymentMethod == 'stripe'">
              <i18n-t keypath="texts.autoPayment" scope="global">
                <template #date
                  ><strong>{{ ctx.formattedRenewalDate }}</strong></template
                >
                <template #storedPaymentMethod>
                  <a href="#" @click.prevent="goToPaymentUpdatePage">{{ $t("words.storedPaymentMethod") }} <lucide-icon name="square-arrow-out-up-right" /></a>
                </template>
              </i18n-t>

              <div v-if="loadingPaymentMethodChange" class="subscription-details-view__payment-loading-spinner">
                <div>{{ $t("messages.loadingPaymentPortal") }}</div>
                <loading-spinner />
              </div>
            </div>
            <div v-else>
              <i18n-t keypath="texts.autoPaymentInvoice" scope="global">
                <template #date
                  ><strong>{{ ctx.formattedRenewalDate }}</strong></template
                >
              </i18n-t>
            </div>
          </template>
          <div v-else>
            {{ $t("texts.customPrices") }}
          </div>
        </div>
        <div v-if="!renews && subscriptionRenewalRequest" class="background-dark p-6 pt-2" style="overflow: hidden">
          <h3 class="label">
            {{ $t("headings.renewal") }}
          </h3>
          <subscription-request-card
            :request="subscriptionRenewalRequest"
          />
        </div>
      </article>

      <article class="subscription-details-view__card subscription-details-view__grid-seats column items-start">
        <div>
          <h1 class="label mt-0 mb-3">{{ $t("headings.users") }}</h1>
          <div>
            <i18n-t
              v-if="ctx.subscription.seats"
              tag="span"
              keypath="labels.countUsers"
              :plural="ctx.subscription.seats"
              class="text-18"
              scope="global"
            >
              <template #count>
                <strong>{{ ctx.subscription.seats }}</strong>
              </template>
            </i18n-t>

            <span v-if="ctx.organization" class="ml-2">
              –
              {{
                $t("texts.xOfxInUse", {
                  x: `${ctx.organization.members.length} / ${ctx.subscription.seats}`
                })
              }}
            </span>
          </div>

          <div
            v-if="renews && ctx.subscription.seats != ctx.subscription.nextRenew.seats"
            class="mt-2 background-warning p-2 color-warning"
          >
            <lucide-icon name="triangle-alert" /> {{
              $t("texts.seatsOnRenew", {
                seats: ctx.subscription.nextRenew.seats,
                date: ctx.formattedRenewalDate
              })
            }}
          </div>
        </div>

        <router-link
          v-if="!ctx.expired && ctx.subscription.editable"
          :to="{
            name: 'subscription-modify',
            params: { subscriptionId: ctx.subscription.id }
          }"
          class="button button_secondary mt-auto"
        >
          {{ $t("actions.changeUsers") }}
        </router-link>
      </article>

      <article
        v-if="ctx.subscription.plan == 'organization' && ctx.subscription.editable || ctx.subscription.upgrades.length > 0"
        class="subscription-details-view__card subscription-details-view__grid-upgrades column items-start"
      >
        <h1 class="label mt-0 mb-3">{{ $t("headings.upgrades") }}</h1>
        <div class="column gap-4">
          <div v-for="upgrade of ctx.subscription.upgrades" :key="upgrade">
            <strong>{{ $t(`upgrades.${upgrade}.name`) }}</strong>
            <p class="mt-1 mb-0">{{ $t(`upgrades.${upgrade}.details`) }}</p>
            <div
              v-if="renews && !ctx.subscription.nextRenew.upgrades.includes(upgrade)"
              class="mt-2 background-warning p-2 color-warning"
            >
              <lucide-icon name="triangle-alert" /> {{
                $t("texts.upgradeCanceled", {
                  date: ctx.formattedRenewalDate
                })
              }}
            </div>
          </div>
        </div>

        <router-link
          v-if="!ctx.expired && ctx.subscription.editable"
          :to="{
            name: 'subscription-modify',
            params: { subscriptionId: ctx.subscription.id }
          }"
          class="button button_secondary mt-auto"
        >
          {{ $t("actions.changeUpgrades") }}
        </router-link>
      </article>

      <article class="subscription-details-view__card subscription-details-view__grid-address">
        <div class="flex">
          <h1 class="label mt-0 mb-3">{{ $t("headings.billingAddress") }}</h1>
          <button
            v-if="!ctx.subscription.expired"
            type="button"
            class="button-icon p-0 ml-auto"
            @click="editingAddress = !editingAddress"
          >
            <lucide-icon :name="editingAddress ? 'x' : 'pencil'" />
          </button>
        </div>
        <div>
          <div v-if="!editingAddress">
            <div
              v-if="ctx.subscription.billingAddress"
              class="subscription-details-view__address"
            >
              <div v-if="ctx.subscription.billingAddress.company">
                {{ ctx.subscription.billingAddress.company }}
              </div>
              <div>
                {{ ctx.subscription.billingAddress.firstname }}
                {{ ctx.subscription.billingAddress.lastname }}
              </div>
              <div>{{ ctx.subscription.billingAddress.street }}</div>
              <div v-if="ctx.subscription.billingAddress.supplement">
                {{ ctx.subscription.billingAddress.supplement }}
              </div>
              <div>
                {{ ctx.subscription.billingAddress.zipcode }}
                {{ ctx.subscription.billingAddress.city }}
              </div>
              <div>
                {{ codeToName(ctx.subscription.billingAddress.country) }}
              </div>

              <div v-if="ctx.subscription.billingAddress.vatId" class="mt-2">
                {{ $t("labels.vatId") }}{{ $t(":") }}
                {{ ctx.subscription.billingAddress.vatId }}
              </div>
            </div>
            <p v-else class="message-info">
              {{ $t("errors.noAddress") }}
            </p>
          </div>
          <form v-else @submit.prevent="updateAddress">
            <address-form-fields
              v-model:firstname="newAddress.firstname"
              v-model:lastname="newAddress.lastname"
              v-model:company="newAddress.company"
              v-model:vatId="newAddress.vatId"
              v-model:street="newAddress.street"
              v-model:supplement="newAddress.supplement"
              v-model:zipcode="newAddress.zipcode"
              v-model:city="newAddress.city"
              v-model:country="newAddress.country"
              :company-required="false"
              :show-form-errors="true"
            />
            <button class="button mt-4" type="submit">
              {{ $t("actions.save") }}
            </button>
          </form>
        </div>
      </article>

      <plan-card :plan-id="ctx.subscription.plan" class="subscription-details-view__grid-plan">
        <template #before>
          <div class="pl-5">
            <h1 class="label mt-0 mb-3">{{ $t("headings.plan") }}</h1>
          </div>
        </template>
      </plan-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useCountries } from "@/helpers/countries";
import { post } from "@/api/API";
import { showAlert, showConfirm, showError, showMessage } from "@core/ui/alert/alert";

import LucideIcon from "@core/ui/LucideIcon.vue";
import TotalCosts from "@manager/purchasing/TotalCosts.vue";
import AddressFormFields from "@manager/common/AddressFormFields.vue";
import PlanCard from "@manager/plans/PlanCard.vue";
import LoadingSpinner from "@core/ui/LoadingSpinner.vue";

import SubscriptionRequestCard from "./SubscriptionRequestCard.vue";

import { usePlans } from "@manager/plans/plans.store";
import { useSubscriptions } from "@/api/subscriptions.store";
import { useOrganizations } from "@manager/organizations/organizations.store";
import { useSubscription } from "./useSubscription";

import type { SubscriptionRequest } from "@/api/subscriptions.store";

const props = defineProps<{
  subscriptionId: number;
}>();

const { t } = useI18n();
const { codeToName } = useCountries();
// const router = useRouter();
const toast = useToast();
const route = useRoute();
const subscriptionRequests = ref<SubscriptionRequest[]>([]);

const { loadPlans } = usePlans();
const { getSubscriptions, getSubscriptionRequests } = useSubscriptions();
const { getOrganizations } = useOrganizations({});

loadPlans();

getSubscriptions();
getOrganizations();
getSubscriptionRequests().then((requests) => {
  subscriptionRequests.value = requests;
});

const subscriptionStore = useSubscriptions();
const ctx = useSubscription(() => props.subscriptionId);

// =============================
// RENEWAL

const renews = computed(() => {
  if (!ctx.subscription) return false;
  return ctx.subscription.renewAutomatically && ctx.expired == false;
});

const editingRenewal = ref(false);

async function cancelSubscription() {
  if (
    (await showConfirm(
      t("questions.cancelSubscription"),
      t("actions.disableRenewal")
    )) == false
  ) {
    return;
  }

  try {
    editingRenewal.value = true;
    const updatedSubscription = await subscriptionStore.cancelSubscription(
      props.subscriptionId
    );
    console.log("Subscription canceled", updatedSubscription);
    toast.success(t("messages.subscriptionCanceled"));
  } catch (e) {
    console.error(e);
    showError(e as Error);
  } finally {
    editingRenewal.value = false;
  }
}
async function reactivateSubscriptionRenewal() {
  try {
    editingRenewal.value = true;
    await subscriptionStore.reactivateSubscriptionRenewal(props.subscriptionId);
    toast.success(t("messages.subscriptionRenewalReactivated"));
  } catch (e) {
    console.error(e);
    showError(e as Error);
  } finally {
    editingRenewal.value = false;
  }
}

async function requestExtensionQuote() {
  const alert = await showAlert<{
    years: number;
  }>({
    title: t("actions.requestExtensionQuote"),
    text: t("questions.extensionQuoteYears"),
    confirmText: t("actions.submitRequest"),
    content: [{
      type: "number",
      name: "years",
      label: t("roundUnits.year", 3),
      value: 1,
      min: 1,
      class: "mb-2",
      required: true
    }, {
      type: "text",
      text: t("texts.extensionQuoteNotice"),
      class: "mb-4"
    }],
    denyText: t("labels.abort")
  });

  if (!alert) {
    return;
  }

  const years = alert.years;

  try {
    const request = await subscriptionStore.requestExtensionQuote(
      props.subscriptionId,
      { years }
    );
    console.log("request requested", request);
    subscriptionRequests.value.push(request);
    toast.success(t("messages.extensionQuoteRequested"));
  } catch (e) {
    console.error(e);
    showError(e as Error);
  }
}

const subscriptionRenewalRequest = computed(() => {
  return subscriptionRequests.value.find(
    (request) => request.subscriptionId == props.subscriptionId
  );
});


async function restoreExpiredSubscription() {
  if (!ctx.expired) {
    return;
  }

  if (
    (await showConfirm(
      t("questions.confirmReactivatingSubscription"),
      t("actions.confirmBuyingRenewal"),
      t("labels.no"),
      { title: t("actions.enableRenewal") }
    )) == false
  ) {
    return;
  }

  if (!ctx.subscription) {
    return;
  }
  
  return subscriptionStore.renewSubscription(ctx.subscription);
}

// =============================
// ADDRESS

const editingAddress = ref(false);

const newAddress = reactive({
  firstname: "",
  lastname: "",
  company: "",
  vatId: "",
  street: "",
  supplement: "",
  zipcode: "",
  city: "",
  country: ""
});

watch(
  () => ctx.subscription,
  (value) => {
    newAddress.firstname = value?.billingAddress?.firstname ?? "";
    newAddress.lastname = value?.billingAddress?.lastname ?? "";
    newAddress.company = value?.billingAddress?.company ?? "";
    newAddress.vatId = value?.billingAddress?.vatId ?? "";
    newAddress.street = value?.billingAddress?.street ?? "";
    newAddress.supplement = value?.billingAddress?.supplement ?? "";
    newAddress.zipcode = value?.billingAddress?.zipcode ?? "";
    newAddress.city = value?.billingAddress?.city ?? "";
    newAddress.country = value?.billingAddress?.country ?? "";
  },
  { immediate: true }
);

async function updateAddress() {
  try {
    await subscriptionStore.updateSubscription(props.subscriptionId, {
      billingAddress: newAddress
    });
    editingAddress.value = false;
  } catch (error) {
    console.log(error);
    showError(error as Error);
  }
}


// =============================
// PAYMENT

const loadingPaymentMethodChange = ref(false);

async function goToPaymentUpdatePage() {
  loadingPaymentMethodChange.value = true;
  try {
    const session = await post<{ url: string; sessionId: string }>(`/v1/subscriptions/${props.subscriptionId}/stripe_modify_payment_method`);
    // navigate to the session URL
    location = session.url as (string & Location);
  
    return session;
  } catch (e) {
    await showError(e as Error);
    loadingPaymentMethodChange.value = false;
  }
}

onMounted(() => {
  if (route.query.payment_change === "succeeded") {
    showMessage(t("messages.paymentMethodUpdated"));
    // remove the query parameter
    history.replaceState({}, document.title, location.pathname);
  }
  else if (route.query.payment_change === "canceled") {
    showError(t("errors.paymentMethodUpdateCanceled"));
    // remove the query parameter
    history.replaceState({}, document.title, location.pathname);
  }
});
</script>

<style lang="scss">
.subscription-details-view {
  container: subscription-details-view / inline-size;

  &__card {
    box-shadow: 0 2px 5px var(--color-shadow-1);
    border-radius: 5px;
    padding: 1.5rem;
    background-color: var(--color-background-deep);
  }

  &__grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr;

  }
  @container (min-width: 55rem) {
    &__grid {
      grid-template-columns: 1fr 1fr;

      &-renewal {
        grid-column: span 2;
      }
      &-seats {
        grid-column: span 1;
      }
      &-upgrades {
        grid-column: span 1;
      }
      &-address {
        grid-column: span 1;
      }
      &-plan {
        grid-column: span 1;
        grid-row: span 3;
      }
    }
  }
  @container (min-width: 84rem) {
    &__grid {
      grid-template-columns: 1fr 1fr 1fr;
    }
    &__grid-plan {
      grid-column: 3 / 4;
      grid-row: 1 / 4;
    }
  }

  &__grid-renewal {
    display: grid;
    grid-template-columns: 1fr;
  }
  @container (min-width: 53rem) {
    &__grid-renewal {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__next-payment-date {
    font-size: 2.5rem;
    font-weight: 700;
  }

  &__auto-payment {
    line-height: 1.5;
    color: var(--color-text-medium);
  }

  &__payment-loading-spinner {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    background-color: var(--color-background-deep);
    z-index: 1000;
    gap: 1rem;
    animation: fade-background-in 300ms;
    font-size: 1.25rem;
  }
  @keyframes fade-background-in {
    from {
      background-color: rgba(0, 0, 0, 0);
    }
    to {
      background-color: var(--color-background-deep);
    }
  }

  &__address {
    line-height: 1.4;
  }
}
</style>
